import { useDebounceCallback } from 'custom_hooks/useDebounceCallback';
import { useCallback, useState } from 'react';
import { AutoCompleteValue } from 'shared/autocomplete/AutoComplete';
import { getInputValue } from 'shared/autocomplete/shared';

export type UseAutoCompleteProps<TData> = Readonly<{
    renderString: (item: TData) => string;
    setFilter: (
        filterValue: React.SetStateAction<
            Readonly<{
                nameSearch?: string;
            }>
        >,
    ) => void;
}>;

export type AutoCompleteInfo<TData> = Readonly<{
    value: AutoCompleteValue<TData>;
    onChange: (newValue: AutoCompleteValue<TData>, onChangeCallback?: () => void) => void;
    inputValue: string;
    onInputValueChange: (newInputValue: string) => void;
    reset: () => void;
    clear: () => void;
}>;

export function useAutoComplete<TData>({
    renderString,
    setFilter,
}: UseAutoCompleteProps<TData>): AutoCompleteInfo<TData> {
    const [value, setValue] = useState<AutoCompleteValue<TData>>(null);
    const [inputValue, setInputValue] = useState('');

    const debouncedSetFilter = useDebounceCallback(200, (nameSearch: string) =>
        setFilter((filter) => ({ ...filter, nameSearch })),
    );

    const onInputValueChange = useCallback(
        (newValue: string) => {
            setInputValue(newValue);
            debouncedSetFilter(newValue);
        },
        [debouncedSetFilter],
    );

    const onChange = useCallback(
        (newValue: AutoCompleteValue<TData>, onChangeCallback?: () => void) => {
            setValue(newValue);
            setInputValue(getInputValue(newValue, renderString));
            onChangeCallback?.();
        },
        [renderString],
    );

    const reset = useCallback(() => {
        setInputValue(getInputValue(value, renderString));
        setFilter((filter) => ({ ...filter, nameSearch: '' }));
    }, [renderString, setFilter, value]);

    const clear = useCallback(() => {
        setInputValue('');
        setFilter((filter) => ({ ...filter, nameSearch: '' }));
        onChange(null);
    }, [onChange, setFilter]);

    return {
        value,
        onChange,
        inputValue,
        onInputValueChange,
        reset,
        clear,
    };
}
